import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Boxes, ShoppingBag, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Seo } from "../components/Seo";

function BundleHero({ bundle }) {
  const fallback = bundle.components_detail?.[0]?.image_url || "";
  const [src, setSrc] = useState(bundle.image_url || fallback);
  if (!src) return null;
  // If the hero fails to load, fall back to the first vial image; if that fails too, drop the image area entirely
  const onError = () => setSrc(src !== fallback ? fallback : "");
  return (
    <div className="aspect-[16/9] bg-[#EFEBE6] overflow-hidden relative">
      <img src={src} alt={bundle.name} loading="lazy" decoding="async" onError={onError} className="w-full h-full object-cover" data-testid={`bundle-image-${bundle.id}`} />
      {bundle.savings > 0 && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#2D6A4F] text-white text-[11px] tracking-[0.12em] uppercase font-bold px-3 py-1.5 rounded-none" data-testid={`bundle-savings-${bundle.id}`}>
          <Tag className="w-3 h-3" /> Save ${bundle.savings.toFixed(2)}
        </span>
      )}
    </div>
  );
}

function BundleCard({ bundle, onAdd }) {
  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-hidden flex flex-col" data-testid={`bundle-card-${bundle.id}`}>
      <BundleHero bundle={bundle} />
      <div className="p-6 flex flex-col flex-1">
        <h2 className="font-product text-base uppercase tracking-wide text-[#1A1A1A]" data-testid={`bundle-name-${bundle.id}`}>{bundle.name}</h2>
        {bundle.description && <p className="text-sm text-[#6E675F] mt-2 leading-relaxed whitespace-pre-line">{bundle.description}</p>}

        <div className="mt-4 space-y-2" data-testid={`bundle-components-${bundle.id}`}>
          {bundle.components_detail.map((c) => (
            <Link key={c.id} to={`/product/${c.id}`} className="flex items-center gap-3 group">
              <img src={c.image_url} alt={c.name} loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="w-9 h-9 rounded object-cover border border-[#EFEBE6]" />
              <span className="text-sm text-[#1A1A1A] group-hover:text-[#8C7B6B] transition-colors">
                {c.quantity > 1 ? `${c.quantity} × ` : ""}{c.name} <span className="text-[#6E675F]">{c.dosage}</span>
              </span>
              <span className="ml-auto text-xs text-[#6E675F]">${(c.price * c.quantity).toFixed(2)}</span>
            </Link>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-[#DDD7CF] flex items-end justify-between">
          <div>
            {bundle.savings > 0 && (
              <p className="text-sm text-[#6E675F] line-through">${bundle.components_total.toFixed(2)}</p>
            )}
            <p className="text-2xl font-medium text-[#1A1A1A]" data-testid={`bundle-price-${bundle.id}`}>
              ${bundle.bundle_price.toFixed(2)} <span className="text-xs text-[#6E675F]">CAD</span>
            </p>
          </div>
          {bundle.available ? (
            <button
              data-testid={`bundle-add-to-cart-${bundle.id}`}
              onClick={() => onAdd(bundle)}
              className="inline-flex items-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-6 py-3 rounded-none hover:bg-[#75665A] hover:-translate-y-0.5 transition-transform duration-300"
            >
              <ShoppingBag className="w-4 h-4" /> Add Stack
            </button>
          ) : (
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F]" data-testid={`bundle-unavailable-${bundle.id}`}>
              Currently unavailable
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Bundles() {
  const [bundles, setBundles] = useState(null);
  const { addBundle } = useCart();

  useEffect(() => {
    api.get("/bundles").then(({ data }) => setBundles(data)).catch(() => setBundles([]));
  }, []);

  const handleAdd = (bundle) => {
    addBundle(bundle);
    toast.success(`${bundle.name} added to cart`);
  };

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="Bundles" description="Curated peptide stacks at exclusive bundle pricing — recovery, longevity and performance research bundles." path="/bundles" />
      <div className="max-w-2xl mb-12">
        <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-3">Curated Stacks</p>
        <h1 className="font-display text-xl sm:text-3xl uppercase tracking-wide text-[#1A1A1A]">Bundles</h1>
        <p className="text-base text-[#6E675F] mt-4 leading-relaxed">
          Research peptides curated into ready-made stacks — at better pricing than buying each vial separately.
        </p>
      </div>

      {bundles === null ? (
        <div className="flex justify-center py-24" data-testid="bundles-loading">
          <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-[#DDD7CF] rounded-lg" data-testid="bundles-empty">
          <Boxes className="w-10 h-10 text-[#8C7B6B] mx-auto mb-4" />
          <p className="text-[#6E675F]">New stacks are being curated — check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="bundles-grid">
          {bundles.map((b) => (
            <BundleCard key={b.id} bundle={b} onAdd={handleAdd} />
          ))}
        </div>
      )}

      <p className="text-xs text-[#6E675F] mt-16 border-t border-[#DDD7CF] pt-6">
        For laboratory research use only. Not for human or veterinary consumption.
      </p>
    </div>
  );
}
